/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { DiagnosticLogger } from "./DiagnosticLogger";
import { OperationalLogger } from "./OperationalLogger";
import { DiagnosticSeverity, type LogDetails } from "../types";
import { AxiosErrorAdapter } from "../errorMapper/adapters/AxiosErrorAdapter";
import { ErrorCodeUtil } from "../utility/ErrorCodeUtil";
import {
  INTERNAL_RATINGS_DIAGNOSTIC_EVENT,
  InternalRatingsEvent,
  type InternalRatingsEventName
} from "../constants/internalRatingsTelemetryConstants";

/** Caller-supplied internal ratings telemetry fields. */
export type InternalRatingsLogFields = Record<string, string | undefined>;

/**
 * The parts of the `GET /v1/internalRatings` catalog the logger inspects.
 * Both lists are optional: the cached record is not validated on read.
 */
export interface InternalRatingsCatalog {
  ratingType?: ReadonlyArray<unknown>;
  ratingValue?: ReadonlyArray<unknown>;
}

const KEY_EVENT: string = "event";
const KEY_ERROR_CODE: string = "error_code";
const KEY_SUB_ERROR_CODE: string = "sub_error_code";
const KEY_ERROR_TYPE: string = "error_type";
const KEY_RATING_TYPE_COUNT: string = "rating_type_count";
const KEY_RATING_VALUE_COUNT: string = "rating_value_count";

const MESSAGE_EMPTY_RESPONSE: string =
  "Internal ratings catalog carried no rating types or values";
const MESSAGE_LOOKUP_FAILED: string =
  "Internal ratings lookup failed; rating names and limits unavailable";

/**
 * Error codes for the Internal Ratings failure scenarios.
 *
 * - Lookup failure / empty catalog → `FETCH_FAILED`. A failed request also
 *   carries the {@link ErrorCodeUtil} sub-error code of the layer that failed
 *   (`N603`, `G500`, `S6xx`, …).
 *
 * TODO(UAW-2441): unity-client-error-codes has no entry for an internal
 * ratings failure yet. Until one is added there, the code is sent empty —
 * the `error` event convention for a caller with no catalog entry — and the
 * sub-error code still identifies the failing layer.
 */
export const InternalRatingsErrorCode = {
  FETCH_FAILED: ""
} as const;

/**
 * Structured telemetry facade for the Internal Ratings flow: the startup
 * lookup of the rating catalog that rating names and parental rating limits
 * resolve against.
 *
 * The request itself is reported by `PostSigninLogger` (`apiRequest`,
 * `api_name=internalRatings`); this logger reports what the flow did with
 * the result. All details are pre-redacted: never raw error messages.
 */
export class InternalRatingsLogger {
  private static readonly adapter: AxiosErrorAdapter = new AxiosErrorAdapter();

  /**
   * `error_code` / `sub_error_code` for a failed internal ratings request.
   * Each is omitted when empty.
   */
  public static getFailureFields(error: unknown): InternalRatingsLogFields {
    const subErrorCode: string = ErrorCodeUtil.extractSubErrorCode(
      error,
      InternalRatingsLogger.adapter
    );
    return {
      [KEY_ERROR_CODE]: InternalRatingsLogger.nonEmpty(
        InternalRatingsErrorCode.FETCH_FAILED
      ),
      [KEY_SUB_ERROR_CODE]: InternalRatingsLogger.nonEmpty(subErrorCode)
    };
  }

  /**
   * Reports the catalog a lookup resolved. A missing catalog, or one with no
   * rating types or no rating values, is an `empty_response` failure: no
   * rating can be resolved against it.
   */
  public static logRatingsLoaded(catalog: InternalRatingsCatalog | null): void {
    const ratingTypeCount: number = InternalRatingsLogger.countOf(
      catalog === null ? undefined : catalog.ratingType
    );
    const ratingValueCount: number = InternalRatingsLogger.countOf(
      catalog === null ? undefined : catalog.ratingValue
    );
    const details: InternalRatingsLogFields = {
      [KEY_RATING_TYPE_COUNT]: String(ratingTypeCount),
      [KEY_RATING_VALUE_COUNT]: String(ratingValueCount)
    };

    if (ratingTypeCount === 0 || ratingValueCount === 0) {
      InternalRatingsLogger.log(
        InternalRatingsEvent.EmptyResponse,
        MESSAGE_EMPTY_RESPONSE,
        DiagnosticSeverity.Warning,
        {
          ...details,
          [KEY_ERROR_CODE]: InternalRatingsLogger.nonEmpty(
            InternalRatingsErrorCode.FETCH_FAILED
          )
        }
      );
      OperationalLogger.logError({
        errorCode: InternalRatingsErrorCode.FETCH_FAILED,
        errorMessage: MESSAGE_EMPTY_RESPONSE
      });
      return;
    }

    InternalRatingsLogger.log(
      InternalRatingsEvent.RatingsLoaded,
      "Internal ratings resolved",
      DiagnosticSeverity.Info,
      details
    );
  }

  /**
   * A lookup failed and was handled silently: startup carries on without
   * the catalog, so rating names render blank and rating limits cannot be
   * evaluated.
   */
  public static logLookupFailed(error: unknown): void {
    const failureFields: InternalRatingsLogFields =
      InternalRatingsLogger.getFailureFields(error);
    InternalRatingsLogger.log(
      InternalRatingsEvent.LookupFailed,
      MESSAGE_LOOKUP_FAILED,
      DiagnosticSeverity.Warning,
      {
        ...failureFields,
        [KEY_ERROR_TYPE]: OperationalLogger.describeErrorType(error)
      }
    );
    OperationalLogger.logError({
      errorCode: InternalRatingsLogger.toDisplayCode(failureFields),
      errorMessage: MESSAGE_LOOKUP_FAILED
    });
  }

  /**
   * `<code>-<sub>` as a user or support would see it, or the bare code when
   * there is no sub-error code. Empty while the code itself is unassigned: a
   * bare sub-error code is not a catalog code.
   */
  private static toDisplayCode(fields: InternalRatingsLogFields): string {
    const errorCode: string | undefined = fields[KEY_ERROR_CODE];
    if (errorCode === undefined) {
      return "";
    }
    const subErrorCode: string | undefined = fields[KEY_SUB_ERROR_CODE];
    return subErrorCode !== undefined
      ? errorCode + "-" + subErrorCode
      : errorCode;
  }

  private static countOf(list: ReadonlyArray<unknown> | undefined): number {
    return Array.isArray(list) ? list.length : 0;
  }

  private static nonEmpty(value: string): string | undefined {
    return value.length > 0 ? value : undefined;
  }

  private static log(
    event: InternalRatingsEventName,
    message: string,
    severity: DiagnosticSeverity,
    extra?: InternalRatingsLogFields
  ): void {
    const details: LogDetails = { [KEY_EVENT]: event };
    if (extra !== undefined) {
      Object.keys(extra).forEach((key: string): void => {
        const value: string | undefined = extra[key];
        if (value !== undefined) {
          details[key] = value;
        }
      });
    }
    DiagnosticLogger.log(
      INTERNAL_RATINGS_DIAGNOSTIC_EVENT,
      message,
      severity,
      details
    );
  }
}
